import React, { useState, useEffect } from 'react';
import { targetAppStore, TargetAppState, appFlightRecorder } from '../../target-app/store';
import { Database, RefreshCw, AlertCircle, Check, Activity, Clock, MousePointer } from 'lucide-react';

export const StateInspector: React.FC = () => {
  const [storeState, setStoreState] = useState<TargetAppState>(targetAppStore.getState());
  const [viewMode, setViewMode] = useState<'state' | 'flightRecorder'>('state');
  const [filterScope, setFilterScope] = useState<'full' | 'cart' | 'discounts' | 'orders'>('full');
  const [recorderLogs, setRecorderLogs] = useState<any[]>(appFlightRecorder.getLogs());

  useEffect(() => {
    const unsub = targetAppStore.subscribe(() => {
      setStoreState(targetAppStore.getState());
      setRecorderLogs([...appFlightRecorder.getLogs()]);
    });
    return () => unsub();
  }, []);

  const getFilteredState = () => {
    switch (filterScope) {
      case 'cart':
        return {
          cart: storeState.cart,
          itemCount: storeState.cart.reduce((a, c) => a + c.quantity, 0),
          subtotal: storeState.cart.reduce((a, c) => a + c.product.price * c.quantity, 0),
        };
      case 'discounts':
        return {
          appliedDiscounts: storeState.appliedDiscounts,
          isBug2Active: storeState.isBug2Active,
        };
      case 'orders':
        return {
          orders: storeState.orders,
          isNull: storeState.orders === null,
          isBug3Active: storeState.isBug3Active,
        };
      case 'full':
      default:
        return storeState;
    }
  };

  const rawJson = JSON.stringify(getFilteredState(), null, 2);

  // Detect state anomalies
  const hasNan =
    rawJson.includes('null') &&
    storeState.cart.some((i) => i.product.price === 0 && storeState.isBug1Active);
  const hasNegative = storeState.isBug2Active && storeState.appliedDiscounts.length > 2;
  const hasNullOrders = storeState.orders === null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* View Mode Toggle Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {viewMode === 'state' ? (
            <Database size={14} style={{ color: 'var(--accent)' }} />
          ) : (
            <Activity size={14} style={{ color: 'var(--green)' }} />
          )}
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>
            {viewMode === 'state' ? 'Target App Live Memory Tree' : 'Flight Recorder User Interaction Logs'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div className="tab-bar">
            <button
              className={`tab ${viewMode === 'state' ? 'active' : ''}`}
              onClick={() => setViewMode('state')}
              style={{ fontSize: 11, padding: '3px 8px' }}
            >
              Memory State
            </button>
            <button
              className={`tab ${viewMode === 'flightRecorder' ? 'active' : ''}`}
              onClick={() => {
                setRecorderLogs([...appFlightRecorder.getLogs()]);
                setViewMode('flightRecorder');
              }}
              style={{ fontSize: 11, padding: '3px 8px' }}
            >
              Flight Logs ({recorderLogs.length})
            </button>
          </div>

          <button
            className="btn btn-sm"
            onClick={() => {
              setStoreState(targetAppStore.getState());
              setRecorderLogs([...appFlightRecorder.getLogs()]);
            }}
            title="Refresh state"
          >
            <RefreshCw size={11} />
          </button>
        </div>
      </div>

      {viewMode === 'state' ? (
        <>
          {/* Scope Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 6 }}>
            <div className="tab-bar">
              {(['full', 'cart', 'discounts', 'orders'] as const).map((scope) => (
                <button
                  key={scope}
                  className={`tab ${filterScope === scope ? 'active' : ''}`}
                  onClick={() => setFilterScope(scope)}
                  style={{ textTransform: 'capitalize', fontSize: 11, padding: '2px 8px' }}
                >
                  {scope}
                </button>
              ))}
            </div>
          </div>

          {/* State Anomaly Alert Banner */}
          {(hasNan || hasNegative || hasNullOrders) && (
            <div
              style={{
                background: 'var(--red-soft)',
                border: '1px solid var(--red)',
                borderRadius: 'var(--radius-sm)',
                padding: '8px 12px',
                fontSize: 11,
                color: 'var(--red)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <AlertCircle size={14} style={{ flexShrink: 0 }} />
              <div>
                <strong>Memory Anomaly Detected: </strong>
                {hasNullOrders && 'orders is currently `null` (NullPointer trigger). '}
                {hasNegative && 'cumulative discounts exceed 100% (negative pricing). '}
                {hasNan && 'NaN detected in cart arithmetic calculations. '}
              </div>
            </div>
          )}

          {/* State JSON Block */}
          <div
            className="code-block"
            style={{
              maxHeight: 380,
              overflowY: 'auto',
              position: 'relative',
            }}
          >
            <pre style={{ margin: 0 }}>{rawJson}</pre>
          </div>
        </>
      ) : (
        /* Flight Recorder Blackbox Logs */
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            maxHeight: 400,
            overflowY: 'auto',
            background: '#090d16',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: 10,
          }}
        >
          {recorderLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, fontSize: 11, color: 'var(--text-3)' }}>
              No flight recorder events recorded yet. Interact with the target application to record user actions!
            </div>
          ) : (
            recorderLogs.slice(-15).reverse().map((log, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 10px',
                  background: log.type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                  borderLeft: log.type === 'error' ? '3px solid var(--red)' : '3px solid var(--accent)',
                  borderRadius: 4,
                  fontSize: 11,
                  fontFamily: 'var(--font-mono)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MousePointer size={12} style={{ color: log.type === 'error' ? 'var(--red)' : 'var(--accent)' }} />
                  <span style={{ color: 'var(--text-1)' }}>
                    {log.action || log.message || log.type}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-3)', fontSize: 10 }}>
                  <Clock size={10} />
                  <span>{new Date(log.timestamp).toLocaleTimeString()}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Check size={12} style={{ color: 'var(--accent)' }} />
        Synchronized with <code>window.navigator.modelContext.getStateSnapshot()</code>
      </div>
    </div>
  );
};
